import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid2,
  Switch,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useMutation, useQuery } from "urql";
import {
  notificationSettingsQuery,
  updateNotificationSettingsMutation,
} from "../support/mutations";
import { useNotification } from "../hooks/useNotification";
import { useEffect } from "react";
import { useState } from "react";
import { useAtom } from "jotai";
import { promptAtom } from "../support/atoms";

const TopicName = (topic) => {
  switch (topic) {
    case "APPROVAL":
      return "approvals";
    case "CONFIRMED_TOKEN":
      return "confirmedTokens";
    case "TX":
      return "transactions";
    case "STD_TOKEN":
      return "stdTokens";
    case "DEV_TOKEN":
      return "devTokens";
    case "LIQ_TOKEN":
      return "liqTokens";
    case "KEYWORD":
      return "keywords";
    case "PENDING_TOKEN":
      return "pendingTokens";
    case "VERIFIED_TOKEN":
      return "verifiedTokens";
    case "TOKEN_SOCIALS":
      return "tokenSocials";
    default:
      return false;
  }
};

const NotificationSettingsPrompt = ({ notificationSettings }) => {
  const { showNotification } = useNotification();
  const [, setPrompt] = useAtom(promptAtom);
  const [state, setState] = useState({
    PENDING_TOKEN: false,
    CONFIRMED_TOKEN: false,
    VERIFIED_TOKEN: false,
    TOKEN_SOCIALS: false,
  });
  const [, updateSettings] = useMutation(updateNotificationSettingsMutation);

  const handleChange = async (event) => {
    let topic = event.target.name;
    let set = event.target.checked;
    await updateSettings({
      input: {
        networkId: 1,
        topic,
        set,
      },
    })
      .catch((err) => {
        showNotification(err, {
          severity: "error",
          autoHideDuration: 3000,
        });
      })
      .then((res) => {
        if (res.error) {
          showNotification(res.error.toString(), {
            severity: "error",
            autoHideDuration: 3000,
          });
        } else {
          showNotification("notifications " + (set ? "enabled" : "disabled"), {
            severity: "success",
            autoHideDuration: 3000,
          });
          setState((prev) => ({ ...prev, [topic]: set }));
        }
      });
  };

  useEffect(() => {
    if (notificationSettings) {
      setState({
        PENDING_TOKEN: notificationSettings[TopicName("PENDING_TOKEN")],
        CONFIRMED_TOKEN: notificationSettings[TopicName("CONFIRMED_TOKEN")],
        VERIFIED_TOKEN: notificationSettings[TopicName("VERIFIED_TOKEN")],
        TOKEN_SOCIALS: notificationSettings[TopicName("TOKEN_SOCIALS")],
      });
    }
  }, [notificationSettings]);

  return (
    <Grid2 container spacing={1} sx={{ margin: 2, marginBottom: 0 }}>
      <Grid2 size={12} alignContent={"center"}>
        <FormControl component="fieldset" variant="standard">
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={state.PENDING_TOKEN}
                  onChange={handleChange}
                  name="PENDING_TOKEN"
                />
              }
              label="Pending"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={state.CONFIRMED_TOKEN}
                  onChange={handleChange}
                  name="CONFIRMED_TOKEN"
                />
              }
              label="Confirmed"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={state.VERIFIED_TOKEN}
                  onChange={handleChange}
                  name="VERIFIED_TOKEN"
                />
              }
              label="Verified"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={state.TOKEN_SOCIALS}
                  onChange={handleChange}
                  name="TOKEN_SOCIALS"
                />
              }
              label="Token Socials"
            />
          </FormGroup>
        </FormControl>
      </Grid2>
      <Grid2 size={"grow"} />
      <Grid2 size={"auto"} alignContent={"center"}>
        <Button
          sx={{ width: 70 }}
          variant="outlined"
          color="default"
          onClick={() => setPrompt((prev) => ({ ...prev, isOpen: false }))}
        >
          Cancel
        </Button>
      </Grid2>
    </Grid2>
  );
};

const NotificationToggleButton = ({ topic }) => {
  const [, setPrompt] = useAtom(promptAtom);
  const { showNotification } = useNotification();
  const [newToggleValue, setNewToggleValue] = useState();
  const [loading, setLoading] = useState(false);
  const [{ data, fetching, error }, refetchSettings] = useQuery({
    query: notificationSettingsQuery,
    variables: {
      networkId: 1,
    },
  });
  const [, updateSettings] = useMutation(updateNotificationSettingsMutation);
  const settings = data?.notificationSettings;

  const toggleNotificationSetting = () => {
    if (topic === "TOKEN")
      setPrompt({
        maxWidth: "xs",
        isOpen: true,
        title: "Notification Settings",
        component: (
          <NotificationSettingsPrompt notificationSettings={settings} />
        ),
      });
    else
      setNewToggleValue(async (prev) => {
        prev = !prev;
        setLoading(true);
        return await updateSettings({
          input: {
            networkId: 1,
            topic,
            set: prev,
          },
        })
          .catch((err) => {
            console.log(err);
            showNotification(err.toString(), {
              severity: "error",
              autoHideDuration: 3000,
            });
            return !prev;
          })
          .then((res) => {
            if (res.error) {
              showNotification(res.error.toString(), {
                severity: "error",
                autoHideDuration: 3000,
              });
              return !prev;
            } else {
              showNotification(
                "notifications " + (prev ? "enabled" : "disabled"),
                {
                  severity: "success",
                  autoHideDuration: 3000,
                }
              );
              return prev;
            }
          })
          .finally(() => {
            setLoading(false);
          });
      });
  };

  useEffect(() => {
    if (settings) {
      setNewToggleValue(settings[TopicName(topic)]);
    }
  }, [settings]);

  return (
    <Button
      aria-label="notfications"
      disabled={newToggleValue === undefined && topic !== "TOKEN"}
      onClick={toggleNotificationSetting}
      variant="outlined"
      color="info"
      sx={{ height: "100%" }}
    >
      {newToggleValue || topic === "TOKEN" ? (
        <NotificationsActiveIcon />
      ) : (
        <NotificationsOffIcon />
      )}
    </Button>
  );
};

export default NotificationToggleButton;
